'use client';

import { ReactNode, useEffect, useState } from 'react';

import { LOADING_TEXTS } from '@/lib/constants/content-section';
import { GradientBubbleColor } from '@/lib/types/content';
import { windowScrollToBottom } from '@/lib/utils/page-control';

export const TailBubble = ({
  text,
  maxWidth = '100%',
  color = 'basic',
  clickAction
}: {
  text: string;
  maxWidth?: string;
  color?: GradientBubbleColor;
  clickAction?: () => void;
}) => {
  const gradientAfter = 'after:absolute after:top-0 after:left-0 after:w-full after:h-full after:rounded-bzc-large';
  const colorClass: Record<GradientBubbleColor, string> = {
    basic: '',
    orange: 'text-gray-alpha-inverse-0 after:bg-gradient-to-r after:from-transparent after:to-orange-100',
    green: 'text-gray-alpha-inverse-0 after:bg-gradient-to-r after:from-transparent after:to-green-100',
    blue: 'text-gray-alpha-inverse-0 after:bg-gradient-to-r after:from-transparent after:to-blue-100',
    olive: 'text-gray-alpha-inverse-0 after:bg-gradient-to-r after:from-transparent after:to-olive-100'
  };
  const maxWidthClass: Record<string, string> = {
    '75%': 'max-w-[75%]',
    '82%': 'max-w-[82%]',
    '90%': 'max-w-[90%]',
    '100%': 'max-w-full'
  };
  return (
    <div
      className={`relative cursor-pointer select-none transition-all duration-250 text-bubble-transition-timing hover:scale-[0.985] w-fit ${maxWidthClass[maxWidth] ?? ''}`}
      onClick={() => {
        if (clickAction) {
          clickAction();
        }
      }}
    >
      <div
        className={`flex items-start ml-[5px] bg-neutral-background-low hover:bg-neutral-background-high text-neutral-background-low hover:text-neutral-background-high rounded-bzc-large overflow-hidden ${gradientAfter} ${colorClass[color] ?? ''}`}
      >
        <div className={'absolute inline-flex bottom-0 left-0 w-[22px] h-[22px]'}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M5 0H22C22 12.5 9 22 0 22C5 17 5 11.5 5 0Z" fill="currentColor" />
          </svg>
        </div>
        <div className={'body-medium-medium py-normal-medium px-normal-large text-neutral-foreground-main w-fit z-[1]'}>{text}</div>
      </div>
    </div>
  );
};

export const TextBubble = ({ text, textClassName, clickAction }: { text: ReactNode; textClassName?: string; clickAction?: () => void }) => {
  return (
    <div
      className={`relative select-none transition-all duration-250 text-bubble-transition-timing w-fit max-w-[75%] bg-neutral-background-low text-neutral-background-low  rounded-bzc-xlarge overflow-hidden ${clickAction ? 'cursor-pointer hover:scale-[0.985] hover:bg-neutral-background-high hover:text-neutral-background-high' : ''}`}
      onClick={() => {
        if (clickAction) {
          clickAction();
        }
      }}
    >
      <div
        className={`py-normal-small px-normal-large  w-fit z-[1] text-neutral-foreground-main text-bubble-overflow-wrap  ${textClassName ?? 'body-medium-medium'}`}
      >
        {text}
      </div>
    </div>
  );
};

export const LoadingTextBubble = () => {
  const [currentText, setCurrentText] = useState(LOADING_TEXTS[0]);
  const [isHideText, setIsHideText] = useState(false);

  useEffect(() => {
    const intervalTimer = setInterval(() => {
      setTimeout(() => {
        setIsHideText(true);
        setTimeout(() => {
          setIsHideText(false);
          setCurrentText((prev) => {
            const currentIndex = LOADING_TEXTS.indexOf(prev ?? '');
            const nextIndex = (currentIndex + 1) % LOADING_TEXTS.length;
            return LOADING_TEXTS[nextIndex];
          });
        }, 300);
      }, 300);
    }, 3000);

    requestAnimationFrame(() => windowScrollToBottom(true));

    return () => {
      clearInterval(intervalTimer);
    };
  }, []);

  return (
    <div className={'loading-gradient text-bubble-transition-timing mb-normal-large'}>
      <span className={`transition-opacity text-bubble-transition-timing duration-300 ${isHideText ? 'opacity-0' : 'opacity-100'}`}>{currentText}</span>
    </div>
  );
};

export const AccuracyBadge = ({ icon, state }: { icon: ReactNode; state: string }) => {
  return (
    <div
      className={`flex items-center justify-center py-tiny-large px-normal-small gap-tiny-medium bg-neutral-background-lowest text-neutral-background-low  rounded-full overflow-hidden relative select-none transition-all duration-250 text-bubble-transition-timing w-fit max-w-[75%]`}
    >
      {icon}
      <div className={`w-fit z-[1] text-neutral-foreground-main caption-medium-semibold`}>
        정확도 <span className={'text-primary-foreground-main'}>{state}</span>
      </div>
    </div>
  );
};
